import React, { useState, useCallback } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { TableProps } from "./types";
import { TableHeader } from "./TableHeader";
import { TableBody } from "./TableBody";
import { TableSkeletonBody } from "./TableSkeletonBody";
import { TableSearchBar } from "./TableSearchBar";
import { useTableSort } from "./hooks/useTableSort";
import { useTableSearch } from "./hooks/useTableSearch";

export function Table<T>({
  data,
  columns,
  keyExtractor,
  onRowPress,
  rowActions,
  minActionSlots,
  onEdit,
  canEdit,
  onDelete,
  canDelete,
  loading = false,
  loadingRowCount = 5,
  emptyMessage = "No data",
  emptyIcon = "table-off",
  sortable = true,
  defaultSort,
  onSortChange,
  rowStyle,
  headerStyle,
  containerStyle,
  stickyHeader = true,
  searchable = false,
  searchValue: controlledSearchValue,
  onSearchChange,
  searchPlaceholder,
  searchDebounceMs,
  selectable,
  selectedKeys,
  onSelectionChange,
  showTooltipWhileEditing,
}: TableProps<T>) {
  const { colors } = useTheme();

  // Search hook - filters data first
  const { searchValue, setSearchValue, filteredData, isSearching } = useTableSearch(data, {
    columns,
    controlledValue: controlledSearchValue,
    onSearchChange,
  });

  // Sort hook - sorts filtered data
  const { sort, handleSort, sortedData } = useTableSort(filteredData, columns, defaultSort, onSortChange);

  // Calculate if actions column should show (same logic as TableRow)
  const hasEditableColumns = columns.some((c) => c.editable);
  const showActionsColumn = !!(rowActions?.length || (hasEditableColumns && onEdit) || onDelete);

  // Selection state calculations
  const visibleKeys = sortedData.map((row, index) => keyExtractor(row, index));
  const selectedCount = selectedKeys ? visibleKeys.filter((key) => selectedKeys.has(key)).length : 0;
  const allSelected = selectable && visibleKeys.length > 0 && selectedCount === visibleKeys.length;
  const someSelected = selectable && selectedCount > 0 && selectedCount < visibleKeys.length;

  // Track last selected index for shift-click range selection
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      // Deselect all visible
      const newSet = new Set(selectedKeys);
      visibleKeys.forEach((key) => newSet.delete(key));
      onSelectionChange(newSet);
    } else {
      // Select all visible
      const newSet = new Set(selectedKeys);
      visibleKeys.forEach((key) => newSet.add(key));
      onSelectionChange(newSet);
    }
  };

  // Handle individual row selection with shift-click range support
  const handleToggleSelect = useCallback(
    (key: string, index: number, shiftKey?: boolean) => {
      if (!onSelectionChange) return;

      if (shiftKey && lastSelectedIndex >= 0) {
        // Range selection: select all rows between last selected and current
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const newSet = new Set(selectedKeys);
        for (let i = start; i <= end; i++) {
          newSet.add(visibleKeys[i]);
        }
        onSelectionChange(newSet);
      } else {
        // Single toggle
        setLastSelectedIndex(index);
        const newSet = new Set(selectedKeys);
        if (newSet.has(key)) {
          newSet.delete(key);
        } else {
          newSet.add(key);
        }
        onSelectionChange(newSet);
      }
    },
    [onSelectionChange, selectedKeys, lastSelectedIndex, visibleKeys],
  );

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }, containerStyle]}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
          <View style={styles.tableContainer}>
            <TableHeader
              columns={columns}
              sort={null}
              onSort={() => {}}
              sortable={false}
              style={headerStyle}
              rowActions={rowActions}
              showActionsColumn={showActionsColumn}
              minActionSlots={minActionSlots}
              selectable={selectable}
            />
            <TableSkeletonBody
              columns={columns}
              rowCount={loadingRowCount}
              showActionsColumn={showActionsColumn}
              selectable={selectable}
            />
          </View>
        </ScrollView>
      </View>
    );
  }

  // No data at all
  if (data.length === 0) {
    return (
      <View style={[styles.container, styles.centered, containerStyle]}>
        <MaterialCommunityIcons
          name={emptyIcon as any}
          size={48}
          color={colors.textSecondary}
          style={{ opacity: 0.5 }}
        />
        <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>{emptyMessage}</ThemedText>
      </View>
    );
  }

  // Data exists but search returned no results
  if (sortedData.length === 0 && isSearching) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }, containerStyle]}>
        {searchable && (
          <TableSearchBar
            value={searchValue}
            onChangeText={setSearchValue}
            placeholder={searchPlaceholder}
            debounceMs={searchDebounceMs}
          />
        )}
        <View style={styles.centered}>
          <MaterialCommunityIcons
            name="magnify-close"
            size={48}
            color={colors.textSecondary}
            style={{ opacity: 0.5 }}
          />
          <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>No results found</ThemedText>
          <ThemedText style={[styles.emptySubtext, { color: colors.textSecondary }]}>
            Try a different search term
          </ThemedText>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }, containerStyle]}>
      {searchable && (
        <TableSearchBar
          value={searchValue}
          onChangeText={setSearchValue}
          placeholder={searchPlaceholder}
          debounceMs={searchDebounceMs}
        />
      )}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        <View style={styles.tableContainer}>
          {stickyHeader ? (
            <>
              <TableHeader
                columns={columns}
                sort={sort}
                onSort={handleSort}
                sortable={sortable}
                style={headerStyle}
                rowActions={rowActions}
                showActionsColumn={showActionsColumn}
                minActionSlots={minActionSlots}
                selectable={selectable}
                allSelected={allSelected}
                someSelected={someSelected}
                onSelectAll={handleSelectAll}
              />
              <ScrollView showsVerticalScrollIndicator={false}>
                <TableBody
                  data={sortedData}
                  columns={columns}
                  keyExtractor={keyExtractor}
                  onRowPress={rowActions ? undefined : onRowPress}
                  rowActions={rowActions}
                  minActionSlots={minActionSlots}
                  onEdit={onEdit}
                  canEdit={canEdit}
                  onDelete={onDelete}
                  canDelete={canDelete}
                  rowStyle={rowStyle}
                  selectable={selectable}
                  selectedKeys={selectedKeys}
                  onToggleSelect={handleToggleSelect}
                  showTooltipWhileEditing={showTooltipWhileEditing}
                />
              </ScrollView>
            </>
          ) : (
            <ScrollView showsVerticalScrollIndicator={false}>
              <TableHeader
                columns={columns}
                sort={sort}
                onSort={handleSort}
                sortable={sortable}
                style={headerStyle}
                rowActions={rowActions}
                showActionsColumn={showActionsColumn}
                minActionSlots={minActionSlots}
                selectable={selectable}
                allSelected={allSelected}
                someSelected={someSelected}
                onSelectAll={handleSelectAll}
              />
              <TableBody
                data={sortedData}
                columns={columns}
                keyExtractor={keyExtractor}
                onRowPress={rowActions ? undefined : onRowPress}
                rowActions={rowActions}
                minActionSlots={minActionSlots}
                onEdit={onEdit}
                canEdit={canEdit}
                onDelete={onDelete}
                canDelete={canDelete}
                rowStyle={rowStyle}
                selectable={selectable}
                selectedKeys={selectedKeys}
                onToggleSelect={handleToggleSelect}
                showTooltipWhileEditing={showTooltipWhileEditing}
              />
            </ScrollView>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    minWidth: "100%",
  },
  tableContainer: {
    flex: 1,
    minWidth: "100%",
  },
  centered: {
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 12,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: "500",
  },
  emptySubtext: {
    fontSize: 12,
    opacity: 0.7,
  },
});
